import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { JobSourcesOverview } from '../../types';
import { formatDateTime } from '../../utils/format';
import { atsLabel } from '../../utils/jobBoard';
import { BoardListState, DEFAULT_BOARD_LIST, boardListSearch, formatCount, readAgo } from '../../utils/jobSources';

interface Props {
  overview: JobSourcesOverview;
  /** Filter the boards table to one state or type and bring it into view. */
  onShowBoards: (filter: Partial<Pick<BoardListState, 'state' | 'agency'>>) => void;
}

interface TileProps {
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
  /** Shown on hover, e.g. the exact time behind a relative one. */
  hint?: string;
  tone?: 'default' | 'warning';
  action?: { label: string; onClick: () => void };
}

const Tile: React.FC<TileProps> = ({ label, value, note, hint, tone = 'default', action }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      borderColor: tone === 'warning' ? 'warning.main' : 'divider',
    }}
  >
    <Typography variant="caption" color="text.secondary" component="dt">
      {label}
    </Typography>
    <Box component="dd" sx={{ m: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Tooltip title={hint ?? ''} disableHoverListener={!hint} disableFocusListener={!hint} disableTouchListener={!hint}>
        <Typography
          variant="h5"
          component="span"
          sx={{ fontWeight: 700, lineHeight: 1.3, alignSelf: 'flex-start', color: tone === 'warning' ? 'warning.dark' : 'text.primary' }}
        >
          {value}
        </Typography>
      </Tooltip>
      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}
      {action && (
        <Button size="small" onClick={action.onClick} sx={{ alignSelf: 'flex-start', mt: 'auto', ml: -0.5, pt: 0.5 }}>
          {action.label}
        </Button>
      )}
    </Box>
  </Paper>
);

/** The numbers at the top of the page, and the same split by ATS. */
const SourcesStatus: React.FC<Props> = ({ overview, onShowBoards }) => {
  const { totals, settings, platforms } = overview;
  const off = totals.retired_boards;

  return (
    <Box>
      <Box
        component="dl"
        aria-label="Status"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        <Tile label="Active jobs" value={formatCount(totals.active_jobs)} note="On the job board now" />
        <Tile
          label="Boards"
          value={`${formatCount(totals.active_boards)} on`}
          note={`${formatCount(off)} off, ${formatCount(totals.boards)} in the registry`}
          action={off > 0 ? { label: 'Show boards that are off', onClick: () => onShowBoards({ state: 'off' }) } : undefined}
        />
        {settings.paused ? (
          <Tile label="Due now" value="Paused" tone="warning" note="Nothing is read on schedule while refreshing is paused" />
        ) : (
          <Tile
            label="Due now"
            value={formatCount(totals.due_boards)}
            note="Read on the next scheduler call"
            action={totals.due_boards > 0 ? { label: 'Show due boards', onClick: () => onShowBoards({ state: 'due' }) } : undefined}
          />
        )}
        <Tile
          label="Boards with failures"
          value={formatCount(totals.failing_boards)}
          tone={totals.failing_boards > 0 ? 'warning' : 'default'}
          note={totals.failing_boards > 0 ? 'Still on, last read failed' : 'Every board that is on read fine'}
          action={
            totals.failing_boards > 0 ? { label: 'Show failing boards', onClick: () => onShowBoards({ state: 'failing' }) } : undefined
          }
        />
        <Tile
          label="Freshest board read"
          value={readAgo(totals.last_refreshed_at)}
          hint={formatDateTime(totals.last_refreshed_at)}
          note={formatDateTime(totals.last_refreshed_at) || 'No board has been read yet'}
        />
        <Tile
          label="Stalest board read"
          value={readAgo(totals.stalest_board_at)}
          hint={formatDateTime(totals.stalest_board_at)}
          note={formatDateTime(totals.stalest_board_at) || 'No board has been read yet'}
        />
        <Tile
          label="Forms read"
          value={formatCount(totals.jobs_with_questions)}
          note="Active jobs whose application questions are known"
        />
        <Tile
          label="Forms to read"
          value={formatCount(totals.jobs_awaiting_questions)}
          note="Active jobs on an ATS that publishes the form, not read yet"
        />
        <Tile
          label="Distinct forms"
          value={formatCount(totals.question_sets)}
          note="Jobs that ask the same questions share one"
        />
        <Tile
          label="Agency boards"
          value={formatCount(totals.agency_boards)}
          note="Staffing agencies and job marketplaces, not direct employers"
          action={
            totals.agency_boards > 0 ? { label: 'Show agency boards', onClick: () => onShowBoards({ agency: 'yes' }) } : undefined
          }
        />
        <Tile
          label="Agency listings"
          value={formatCount(totals.agency_jobs)}
          note="Active jobs the job board hides unless agencies are included"
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
        <Table size="small" aria-label="Boards and jobs by ATS" sx={{ '& th, & td': { px: { xs: 1, sm: 2 } } }}>
          <TableHead>
            <TableRow>
              <TableCell>ATS</TableCell>
              <TableCell>Refreshing</TableCell>
              <TableCell align="right">Boards</TableCell>
              <TableCell align="right">Active jobs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {platforms.map((row) => (
              <TableRow key={row.platform}>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  {/* Jumps to the Boards list below, filtered to this ATS. */}
                  <Link
                    component={RouterLink}
                    to={{ search: boardListSearch({ ...DEFAULT_BOARD_LIST, platform: row.platform }), hash: '#sources-boards' }}
                    underline="hover"
                    color="inherit"
                    aria-label={`Show ${atsLabel(row.platform)} boards`}
                  >
                    {atsLabel(row.platform)}
                  </Link>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.enabled ? 'Enabled' : 'Disabled'}
                    color={row.enabled ? 'success' : 'default'}
                    variant={row.enabled ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell align="right">
                  {formatCount(row.active_boards)} on
                  {row.boards > row.active_boards && (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      , {formatCount(row.boards - row.active_boards)} off
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">{formatCount(row.jobs)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SourcesStatus;
